import { Button } from "@/components/ui/button";
import { Menu, LogOut, UserPen } from "lucide-react";
import { useLocation } from "wouter";
import { usePosAuth } from "@/hooks/pos/use-pos-auth";
import ConfirmModal from "./common/confirmation-modal";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { navigate } from "wouter/use-browser-location";
import logo from "@/assets/logo.svg";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface AdminHeaderProps {
  onToggleSidebar: () => void;
}

export default function AdminHeader({ onToggleSidebar }: AdminHeaderProps) {
  const [open, setOpen] = useState(false);
  const { user, logout } = usePosAuth();

  return (
    <div className="bg-white shadow-sm border-b border-gray-200 px-[12px] h-[44px]">
      <div className="flex items-center justify-between h-full">
        <div className="flex flex-row-reverse items-center w-[235px] justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleSidebar}
            className="h-[32px] w-[32px]"
          >
            <Menu className="h-6 w-6" />
          </Button>
          <div className="flex items-center gap-[8px]">
            <div className="w-[32px] h-[32px] flex items-center justify-center">
              <img src={logo} alt="" />
            </div>
            <div onClick={() => navigate("/admin")} className="cursor-pointer">
              <p className="heading text-[16px] font-bold">
                SECH <span className="font-normal">ADMIN</span>
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center">
          <div className="nf-n-un flex items-center gap-[8px]">
            <DropdownMenu>
              <DropdownMenuTrigger asChild className="w-full">
                <Button
                  variant="outline"
                  className="w-[32px] h-[32px] p-0 focus-visible:ring-offset-0 focus:ring-0 focus-visible:ring-0 flex items-center justify-center rounded-full overflow-hidden border-0"
                >
                  <Avatar className="h-[32px] w-[32px]">
                    <AvatarImage
                      src={user?.imageUrl || undefined}
                      alt={user?.displayName || user?.email || "User"}
                      className="object-cover"
                    />
                    <AvatarFallback className="bg-[#FF158A] text-[var(--fixed-light-color)] text-[12px] font-normal">
                      {user?.email?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent
                align="end"
                className="w-[220px] rounded-2xl p-0 shadow-lg border border-slate-200"
              >
                <div className="flex items-center gap-3 px-4 pt-4 pb-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage
                      src={user?.imageUrl || undefined}
                      alt={user?.displayName || user?.email || "User"}
                      className="object-cover"
                    />
                    <AvatarFallback className="bg-[#FF158A] text-white text-sm font-semibold">
                      {user?.email?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col">
                    <span className="text-[13px] font-semibold text-slate-900 leading-tight">
                      {user?.displayName || user?.email}
                    </span>
                    <span className="text-[11px] text-slate-500">
                      {user?.email}
                    </span>
                  </div>
                </div>
                <div className="h-px bg-slate-200 mx-4 mt-1" />

                <button
                  className="flex w-full items-center gap-2 px-4 py-3 text-[13px] text-slate-700 hover:bg-slate-50"
                  onClick={() => navigate(`/admin/pos-users/${user?.id}/edit`)}
                >
                  <UserPen className="h-4 w-4" />
                  <span>Edit Account</span>
                </button>

                <button
                  className="flex w-full items-center gap-2 px-4 py-3 text-[13px] text-slate-700 hover:bg-slate-50"
                  onClick={() => setOpen(true)}
                >
                  <LogOut className="h-4 w-4" />
                  <span>Sign Out</span>
                </button>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      <ConfirmModal
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={async () => {
          await logout();
          navigate("/admin/login");
        }}
        title="Sign Out"
        description="Are you sure you want to Sign Out?"
        confirmText="Sign Out"
        cancelText="Cancel"
        variant="logout"
      />
    </div>
  );
}
